import { Cadet } from "../../../shared/types/scoreboard.types";
import { CalloutPair, CalloutWinnerStatus } from "../types";
import { BUSINESS_RULES } from "../../../shared/constants";
import { ExcelService } from "../../../services/excel/excel.service";
import ExcelJS from "exceljs";

/**
 * Class 3 Callout utility functions - the original callout implementation
 */
export class Class3CalloutUtils {
  /**
   * Calculate C3 recruit + sales total for a cadet
   */
  static calculateC3RecSalesTotal(cadet: Cadet): number {
    return (cadet.c3 && cadet.c3.recruit ? cadet.c3.recruit : 0) + (cadet.c3 && cadet.c3.sales ? cadet.c3.sales : 0);
  }

  /**
   * Calculate previous points for Class 3 (qualification + c1 + c2 totals)
   */
  static calculatePreviousPoints(cadet: Cadet): number {
    const qualification = cadet.qualification ? cadet.qualification : 0;
    const c1Total = cadet.c1 && cadet.c1.total ? cadet.c1.total : 0;
    const c2Total = cadet.c2 && cadet.c2.total ? cadet.c2.total : 0;
    return qualification + c1Total + c2Total;
  }

  /**
   * Determine the winner between two cadets based on C3 recruit + sales
   */
  static determineWinner(cadet1: Cadet, cadet2: Cadet): CalloutWinnerStatus {
    const cadet1Total = Class3CalloutUtils.calculateC3RecSalesTotal(cadet1);
    const cadet2Total = Class3CalloutUtils.calculateC3RecSalesTotal(cadet2);

    // console.log(`${cadet1.primaryCadet}: C3 Rec+Sales = ${cadet1Total}`);
    // console.log(`${cadet2.primaryCadet}: C3 Rec+Sales = ${cadet2Total}`);

    if (cadet1Total > cadet2Total) return CalloutWinnerStatus.CADET1;
    if (cadet2Total > cadet1Total) return CalloutWinnerStatus.CADET2;
    return CalloutWinnerStatus.TIE;
  }

  /**
   * Calculate point transfer amount for Class 3
   */
  static calculatePointTransfer(winner: Cadet, loser: Cadet): number {
    const loserPoints = Class3CalloutUtils.calculatePreviousPoints(loser);
    const transfer = loserPoints * BUSINESS_RULES.CALLOUT.POINT_TRANSFER_RATIO;
    
    // console.log(`Point transfer calculation:`);
    // console.log(`  Loser (${loser.primaryCadet}) previous points: ${loserPoints}`);
    // console.log(`  Transfer ratio: ${BUSINESS_RULES.CALLOUT.POINT_TRANSFER_RATIO}`);
    // console.log(`  Transfer amount: ${transfer}`);
    
    return transfer;
  }

  /**
   * Check Class 3 callout eligibility based on qualification status through Class 2
   * Even if someone becomes disqualified in Class 3, they still participate in Class 3 callout
   * The callout is for those who were in the running through Class 2
   */
  static isEligibleForClass3Callout(cadet: Cadet): boolean {
    // Calculate disqualification status based ONLY on data up to Class 2
    // Anyone qualified through Class 2 can participate in Class 3 callout
    return !ExcelService.calculateDisqualificationStatusUpTo(cadet, 2);
  }

  /**
   * Generate callout pairs for Class 3 based on current total standings
   * Special case for Tian Zhao (only for Class 3)
   * Special pairing: Will Kuo vs Frances Yau (instead of Will Kuo getting a bye)
   */
  static async generateClass3CalloutPairs(cadets: Cadet[]): Promise<CalloutPair[]> {
    // console.log("=== Generating Class 3 Callout Pairings ===");
    
    // Filter cadets based on their qualification status up to Class 3 ONLY
    // Keep special case for Tian Zhao even if disqualified
    const qualifiedCadets = cadets
      .filter(cadet => {
        const eligible = Class3CalloutUtils.isEligibleForClass3Callout(cadet);
        return eligible || cadet.primaryCadet === BUSINESS_RULES.CALLOUT.SPECIAL_CADET_NAME;
      })
      .sort((a, b) => {
        // First sort by rank
        if (a.rank !== b.rank) {
          return a.rank - b.rank;
        }
        // If ranks are equal, sort alphabetically by name
        return a.primaryCadet.localeCompare(b.primaryCadet);
      });

    // console.log(`Found ${qualifiedCadets.length} qualified cadets for Class 3 callout (based on data up to Class 3)`);
    
    // Log which cadets are included
    qualifiedCadets.forEach(cadet => {
      const eligibleForClass3 = Class3CalloutUtils.isEligibleForClass3Callout(cadet);
      if (!eligibleForClass3 && cadet.primaryCadet === BUSINESS_RULES.CALLOUT.SPECIAL_CADET_NAME) {
        // console.log(`Including ${cadet.primaryCadet} - special case (disqualified as of Class 3)`);
      }
    });
    
    const pairs: CalloutPair[] = [];

    // Special logic: Always remove Frances Yau from regular qualified cadets for special pairing
    // This ensures she's always available for the odd pairing, regardless of her qualification status
    const francesYau = cadets.find(cadet => cadet.primaryCadet === "Frances Yau");
    const qualifiedWithoutFrances = qualifiedCadets.filter(cadet => cadet.primaryCadet !== "Frances Yau");
    
    // Check if we have an odd number of qualified cadets (excluding Frances Yau)
    if (qualifiedWithoutFrances.length % 2 === 1) {
      // Odd number - use Frances Yau for special pairing
      if (francesYau) {
        // Generate pairs for all qualified cadets except the last one
        for (let i = 0; i < qualifiedWithoutFrances.length - 1; i += 2) {
          const cadet1 = qualifiedWithoutFrances[i];
          const cadet2 = qualifiedWithoutFrances[i + 1];

          // console.log(`\nPair ${pairs.length + 1}: ${cadet1.primaryCadet} (Rank ${cadet1.rank}) vs ${cadet2.primaryCadet} (Rank ${cadet2.rank})`);
          
          pairs.push({ cadet1, cadet2 });
        }
        
        // Pair the last qualified cadet with Frances Yau
        const lastCadet = qualifiedWithoutFrances[qualifiedWithoutFrances.length - 1];
        pairs.push({ cadet1: lastCadet, cadet2: francesYau });
        // console.log(`\nSpecial pairing: ${lastCadet.primaryCadet} vs Frances Yau (special odd pairing)`);
      } else {
        // Frances Yau not found in data - use regular pairing with bye
        for (let i = 0; i < qualifiedWithoutFrances.length - 1; i += 2) {
          const cadet1 = qualifiedWithoutFrances[i];
          const cadet2 = qualifiedWithoutFrances[i + 1];

          // console.log(`\nPair ${pairs.length + 1}: ${cadet1.primaryCadet} (Rank ${cadet1.rank}) vs ${cadet2.primaryCadet} (Rank ${cadet2.rank})`);
          
          pairs.push({ cadet1, cadet2 });
        }

        // Handle odd number of cadets (Frances Yau not found in data)
        const lastCadet = qualifiedWithoutFrances[qualifiedWithoutFrances.length - 1];
        // console.log(`\nOdd number of cadets - ${lastCadet.primaryCadet} gets a bye (Frances Yau not found in data)`);
      }
    } else {
      // Even number of qualified cadets (excluding Frances Yau) - regular pairing
      // If Frances Yau was qualified, add her back for regular pairing
      const finalQualifiedCadets = francesYau && qualifiedCadets.find(c => c.primaryCadet === "Frances Yau") 
        ? [...qualifiedWithoutFrances, francesYau].sort((a, b) => {
            if (a.rank !== b.rank) return a.rank - b.rank;
            return a.primaryCadet.localeCompare(b.primaryCadet);
          })
        : qualifiedWithoutFrances;
        
      for (let i = 0; i < finalQualifiedCadets.length - 1; i += 2) {
        const cadet1 = finalQualifiedCadets[i];
        const cadet2 = finalQualifiedCadets[i + 1];

        // console.log(`\nPair ${pairs.length + 1}: ${cadet1.primaryCadet} (Rank ${cadet1.rank}) vs ${cadet2.primaryCadet} (Rank ${cadet2.rank})`);
        
        pairs.push({ cadet1, cadet2 });
      }
      
      // Handle case where we still have an odd number after adding Frances back
      if (finalQualifiedCadets.length % 2 === 1) {
        const lastCadet = finalQualifiedCadets[finalQualifiedCadets.length - 1];
        // console.log(`\nOdd number of cadets - ${lastCadet.primaryCadet} gets a bye`);
      }
    }

    // console.log(`\nGenerated ${pairs.length} pairs for Class 3 callout`);
    // console.log("=== End Class 3 Pairing Generation ===\n");
    
    return pairs;
  }

  /**
   * Log pairing information for debugging
   */
  static logPairInfo(cadet1: Cadet, cadet2: Cadet, winner: CalloutWinnerStatus): void {
    const cadet1Total = Class3CalloutUtils.calculateC3RecSalesTotal(cadet1);
    const cadet2Total = Class3CalloutUtils.calculateC3RecSalesTotal(cadet2);
    const cadet1Previous = Class3CalloutUtils.calculatePreviousPoints(cadet1);
    const cadet2Previous = Class3CalloutUtils.calculatePreviousPoints(cadet2);

    // console.log(`\n--- Pairing: ${cadet1.primaryCadet} vs ${cadet2.primaryCadet} ---`);
    // console.log(`${cadet1.primaryCadet} - C3 Rec+Sales: ${cadet1Total}, Previous: ${cadet1Previous}`);
    // console.log(`${cadet2.primaryCadet} - C3 Rec+Sales: ${cadet2Total}, Previous: ${cadet2Previous}`);
    // console.log(`Winner: ${winner}`);
  }

  /**
   * Process Class 3 callout results and update cadet rankings
   */
  static processClass3CalloutResults(cadets: Cadet[], pairs: CalloutPair[]): Cadet[] {
    // console.log("=== Processing Class 3 Callout Results ===");
    
    const updatedCadets = [...cadets];

    // Process each pair and update callout results
    pairs.forEach((pair, index) => {
      // console.log(`\nProcessing pair ${index + 1}: ${pair.cadet1.primaryCadet} vs ${pair.cadet2.primaryCadet}`);
      
      const cadet1 = updatedCadets.find(c => c.primaryCadet === pair.cadet1.primaryCadet);
      const cadet2 = updatedCadets.find(c => c.primaryCadet === pair.cadet2.primaryCadet);
      
      if (!cadet1 || !cadet1.c3 || !cadet2 || !cadet2.c3) {
        // console.log(`Skipping pair - missing C3 data`);
        return;
      }

      const winnerStatus = Class3CalloutUtils.determineWinner(cadet1, cadet2);
      let pointTransfer = 0;

      if (winnerStatus !== CalloutWinnerStatus.TIE) {
        const winner = winnerStatus === CalloutWinnerStatus.CADET1 ? cadet1 : cadet2;
        const loser = winnerStatus === CalloutWinnerStatus.CADET1 ? cadet2 : cadet1;
        pointTransfer = Class3CalloutUtils.calculatePointTransfer(winner, loser);

        // Update winner
        winner.c3.callout = pointTransfer;
        winner.c3.calloutNotes = `Won against ${loser.primaryCadet} (gained ${pointTransfer.toFixed(2)} points)`;
        winner.c3.total += pointTransfer; // Update C3 total to include callout points
        winner.total += pointTransfer;

        // Update loser
        loser.c3.callout = -pointTransfer;
        loser.c3.calloutNotes = `Lost to ${winner.primaryCadet} (lost ${pointTransfer.toFixed(2)} points)`;
        loser.c3.total -= pointTransfer; // Update C3 total to include callout points
        loser.total -= pointTransfer;

        // console.log(`${winner.primaryCadet} wins, gains ${pointTransfer.toFixed(2)} points`);
        // console.log(`${loser.primaryCadet} loses ${pointTransfer.toFixed(2)} points`);
      } else {
        // Handle ties
        cadet1.c3.callout = 0;
        cadet1.c3.calloutNotes = `Tied with ${cadet2.primaryCadet}`;
        cadet2.c3.callout = 0;
        cadet2.c3.calloutNotes = `Tied with ${cadet1.primaryCadet}`;
        
        // console.log(`Tie between ${cadet1.primaryCadet} and ${cadet2.primaryCadet}`);
      }

      // Update disqualification status - must be recalculated after total changes
      [cadet1, cadet2].forEach(cadet => {
        cadet.isDisqualified = ExcelService.calculateDisqualificationStatus(cadet);
        // If disqualified, set rank to 999
        if (cadet.isDisqualified) {
          cadet.rank = 999;
        }
      });
    });

    // Sort and update rankings
    updatedCadets.sort((a, b) => {
      if (a.isDisqualified && b.isDisqualified) {
        return a.primaryCadet.localeCompare(b.primaryCadet);
      }
      if (a.isDisqualified) return 1;
      if (b.isDisqualified) return -1;
      return b.total - a.total || a.primaryCadet.localeCompare(b.primaryCadet);
    });

    // Update ranks
    let currentRank = 1;
    updatedCadets.forEach((cadet, index) => {
      if (cadet.isDisqualified) {
        cadet.rank = 999;
      } else {
        if (index > 0 && cadet.total !== updatedCadets[index - 1].total) {
          currentRank = index + 1;
        }
        cadet.rank = currentRank;
      }
    });

    // console.log("=== End Class 3 Callout Processing ===");
    return updatedCadets;
  }

  /**
   * Export Class 3 callout pairings to Excel for reference
   */
  static async exportClass3CalloutPairings(pairs: CalloutPair[], cadets: Cadet[]): Promise<void> {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Class 3 Callout Pairings');

      // Add title
      worksheet.addRow(['Class 3 Callout Pairings']);
      worksheet.addRow(['Generated based on current standings after Class 2']);
      worksheet.addRow([]); // Empty row

      // Style title
      const titleRow = worksheet.getRow(1);
      titleRow.font = { bold: true, size: 16 };
      
      const subtitleRow = worksheet.getRow(2);
      subtitleRow.font = { italic: true, size: 12 };

      // Add headers
      const headers = [
        'Pair #',
        'Cadet 1 Rank', 'Cadet 1 Name', 'Cadet 1 Total',
        'VS',
        'Cadet 2 Name', 'Cadet 2 Rank', 'Cadet 2 Total',
        'Total Difference'
      ];
      worksheet.addRow(headers);

      // Style headers
      const headerRow = worksheet.getRow(4);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };

      // Add pairing data
      pairs.forEach((pair, index) => {
        const totalDiff = Math.abs(pair.cadet1.total - pair.cadet2.total);
        
        const row = [
          index + 1,
          pair.cadet1.rank === 999 ? 'DQ' : pair.cadet1.rank,
          pair.cadet1.primaryCadet,
          pair.cadet1.total.toFixed(2),
          'VS',
          pair.cadet2.primaryCadet,
          pair.cadet2.rank === 999 ? 'DQ' : pair.cadet2.rank,
          pair.cadet2.total.toFixed(2),
          totalDiff.toFixed(2)
        ];
        worksheet.addRow(row);
      });

      // Handle odd cadet (bye) - use Class 3 eligibility only
      const qualifiedCadets = cadets.filter(cadet => {
        const eligible = Class3CalloutUtils.isEligibleForClass3Callout(cadet);
        return eligible || cadet.primaryCadet === BUSINESS_RULES.CALLOUT.SPECIAL_CADET_NAME;
      }).sort((a, b) => {
        // First sort by rank
        if (a.rank !== b.rank) {
          return a.rank - b.rank;
        }
        // If ranks are equal, sort alphabetically by name
        return a.primaryCadet.localeCompare(b.primaryCadet);
      });

      if (qualifiedCadets.length % 2 === 1) {
        const byeCadet = qualifiedCadets[qualifiedCadets.length - 1];
        worksheet.addRow([]);
        worksheet.addRow([
          'BYE',
          byeCadet.rank === 999 ? 'DQ' : byeCadet.rank,
          byeCadet.primaryCadet,
          byeCadet.total.toFixed(2),
          '(Gets Bye)',
          '', '', '', ''
        ]);
        
        // Style bye row
        const byeRow = worksheet.getRow(worksheet.rowCount);
        byeRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFEAA7' }
        };
      }

      // Add summary section
      worksheet.addRow([]);
      worksheet.addRow(['Summary:']);
      worksheet.addRow([`Total Qualified Cadets: ${qualifiedCadets.length}`]);
      worksheet.addRow([`Total Pairs: ${pairs.length}`]);
      worksheet.addRow([`Cadets with Bye: ${qualifiedCadets.length % 2}`]);

      // Auto-fit columns
      worksheet.columns.forEach((column: any) => {
        column.width = 15;
      });

      // Generate file and trigger download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'c3-callout-pairings.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // console.log(`Exported Class 3 callout pairings to c3-callout-pairings.xlsx`);
    } catch (error) {
      console.error('Error exporting Class 3 callout pairings:', error);
      throw new Error('Failed to export Class 3 callout pairings');
    }
  }

  /**
   * Get preview information for Class 3 callout pairings
   */
  static async getClass3CalloutPreview(cadets: Cadet[]): Promise<{
    totalCadets: number;
    qualifiedCadets: number;
    pairs: number;
    byeCadets: number;
    topPairings: Array<{
      pair: number;
      cadet1: { name: string; rank: number; total: number };
      cadet2: { name: string; rank: number; total: number };
      difference: number;
    }>;
  }> {
    const qualifiedCadets = cadets.filter(cadet => {
      const eligible = Class3CalloutUtils.isEligibleForClass3Callout(cadet);
      return eligible || cadet.primaryCadet === BUSINESS_RULES.CALLOUT.SPECIAL_CADET_NAME;
    }).sort((a, b) => {
      // First sort by rank
      if (a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      // If ranks are equal, sort alphabetically by name
      return a.primaryCadet.localeCompare(b.primaryCadet);
    });

    const pairs = await Class3CalloutUtils.generateClass3CalloutPairs(cadets);
    
    const topPairings = pairs.slice(0, 5).map((pair, index) => ({
      pair: index + 1,
      cadet1: {
        name: pair.cadet1.primaryCadet,
        rank: pair.cadet1.rank,
        total: pair.cadet1.total
      },
      cadet2: {
        name: pair.cadet2.primaryCadet,
        rank: pair.cadet2.rank,
        total: pair.cadet2.total
      },
      difference: Math.abs(pair.cadet1.total - pair.cadet2.total)
    }));

    return {
      totalCadets: cadets.length,
      qualifiedCadets: qualifiedCadets.length,
      pairs: pairs.length,
      byeCadets: qualifiedCadets.length % 2,
      topPairings
    };
  }

  /**
   * Export post-Class 3 callout results to Excel file
   */
  static async exportPostClass3CalloutResults(cadets: Cadet[]): Promise<void> {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Scoreboard');

      // Define headers
      const headers = [
        'Rank', 'Primary Cadet', 'Agent Code', 'Spouse/Partner', 'SMD', 'Qualification',
        'C1 Recruit', 'C1 Sales', 'C1 Book', 'C1 Bonus Promo', 'C1 Bonus BBC', 'C1 Bonus Events', 
        'C1 Bonus BF', 'C1 Bonus GX', 'C1 Attend', 'C1 Penalty', 'C1 Penalty Notes', 'C1 Total',
        'C2 Recruit', 'C2 Sales', 'C2 Book', 'C2 Bonus Promo', 'C2 Bonus BBC', 'C2 Bonus Events', 
        'C2 Bonus BF', 'C2 Bonus GX', 'C2 Attend', 'C2 Penalty', 'C2 Penalty Notes', 'C2 Total',
        'C3 Recruit', 'C3 Sales', 'C3 Book', 'C3 Bonus Income', 'C3 Bonus BBC', 'C3 Bonus Events', 
        'C3 Bonus BF', 'C3 Bonus GX', 'C3 Attend', 'C3 Penalty', 'C3 Penalty Notes', 'C3 Callout', 'C3 Callout Notes', 'C3 Total',
        'C4 Recruit', 'C4 Sales', 'C4 Book', 'C4 Bonus Promo', 'C4 Bonus BBC', 'C4 Bonus Events',
        'C4 Bonus BF', 'C4 Bonus GX', 'C4 Attend', 'C4 Penalty', 'C4 Penalty Notes', 'C4 Callout', 'C4 Callout Notes', 'C4 Total',
        'C5 Recruit', 'C5 Sales', 'C5 Book', 'C5 Bonus Promo', 'C5 Bonus BBC', 'C5 Bonus Events',
        'C5 Bonus BF', 'C5 Bonus GX', 'C5 Attend', 'C5 Penalty', 'C5 Penalty Notes', 'C5 Callout', 'C5 Callout Notes', 'C5 Total',
        'Total'
      ];

      // Add headers
      worksheet.addRow(headers);

      // Style headers
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };

      // Add data rows
      cadets.forEach(cadet => {
        // Helper function to format callout value - empty string if 0
        const formatCallout = (value: number | undefined) => {
          if (!value || value === 0) return '';
          return value;
        };

        const row = [
          cadet.rank === 999 ? 'DQ' : cadet.rank,
          cadet.primaryCadet,
          cadet.agentCode || '',
          cadet.spousePartner || '',
          cadet.smd || '',
          cadet.qualification || 0,
          // C1 data
          cadet.c1 && cadet.c1.recruit ? cadet.c1.recruit : '',
          cadet.c1 && cadet.c1.sales ? cadet.c1.sales : '',
          cadet.c1 && cadet.c1.book ? cadet.c1.book : '',
          cadet.c1 && cadet.c1.bonusPromo ? cadet.c1.bonusPromo : '',
          cadet.c1 && cadet.c1.bonusBBC ? cadet.c1.bonusBBC : '',
          cadet.c1 && cadet.c1.bonusEvents ? cadet.c1.bonusEvents : '',
          cadet.c1 && cadet.c1.bonusBF ? cadet.c1.bonusBF : '',
          cadet.c1 && cadet.c1.bonusGX ? cadet.c1.bonusGX : '',
          cadet.c1 && cadet.c1.attend ? cadet.c1.attend : '',
          cadet.c1 && cadet.c1.penalty ? cadet.c1.penalty : '',
          cadet.c1 && cadet.c1.penaltyNotes ? cadet.c1.penaltyNotes : '',
          cadet.c1 && cadet.c1.total ? cadet.c1.total : '',
          // C2 data
          cadet.c2 && cadet.c2.recruit ? cadet.c2.recruit : '',
          cadet.c2 && cadet.c2.sales ? cadet.c2.sales : '',
          cadet.c2 && cadet.c2.book ? cadet.c2.book : '',
          cadet.c2 && cadet.c2.bonusPromo ? cadet.c2.bonusPromo : '',
          cadet.c2 && cadet.c2.bonusBBC ? cadet.c2.bonusBBC : '',
          cadet.c2 && cadet.c2.bonusEvents ? cadet.c2.bonusEvents : '',
          cadet.c2 && cadet.c2.bonusBF ? cadet.c2.bonusBF : '',
          cadet.c2 && cadet.c2.bonusGX ? cadet.c2.bonusGX : '',
          cadet.c2 && cadet.c2.attend ? cadet.c2.attend : '',
          cadet.c2 && cadet.c2.penalty ? cadet.c2.penalty : '',
          cadet.c2 && cadet.c2.penaltyNotes ? cadet.c2.penaltyNotes : '',
          cadet.c2 && cadet.c2.total ? cadet.c2.total : '',
          // C3 data (includes callout columns)
          cadet.c3 && cadet.c3.recruit ? cadet.c3.recruit : '',
          cadet.c3 && cadet.c3.sales ? cadet.c3.sales : '',
          cadet.c3 && cadet.c3.book ? cadet.c3.book : '',
          cadet.c3 && cadet.c3.bonusPromo ? cadet.c3.bonusPromo : '',
          cadet.c3 && cadet.c3.bonusBBC ? cadet.c3.bonusBBC : '',
          cadet.c3 && cadet.c3.bonusEvents ? cadet.c3.bonusEvents : '',
          cadet.c3 && cadet.c3.bonusBF ? cadet.c3.bonusBF : '',
          cadet.c3 && cadet.c3.bonusGX ? cadet.c3.bonusGX : '',
          cadet.c3 && cadet.c3.attend ? cadet.c3.attend : '',
          cadet.c3 && cadet.c3.penalty ? cadet.c3.penalty : '',
          cadet.c3 && cadet.c3.penaltyNotes ? cadet.c3.penaltyNotes : '',
          formatCallout(cadet.c3 && cadet.c3.callout ? cadet.c3.callout : 0),
          cadet.c3 && cadet.c3.calloutNotes ? cadet.c3.calloutNotes : '',
          cadet.c3 && cadet.c3.total ? cadet.c3.total : '',
          // C4 data
          cadet.c4 && cadet.c4.recruit ? cadet.c4.recruit : '',
          cadet.c4 && cadet.c4.sales ? cadet.c4.sales : '',
          cadet.c4 && cadet.c4.book ? cadet.c4.book : '',
          cadet.c4 && cadet.c4.bonusPromo ? cadet.c4.bonusPromo : '',
          cadet.c4 && cadet.c4.bonusBBC ? cadet.c4.bonusBBC : '',
          cadet.c4 && cadet.c4.bonusEvents ? cadet.c4.bonusEvents : '',
          cadet.c4 && cadet.c4.bonusBF ? cadet.c4.bonusBF : '',
          cadet.c4 && cadet.c4.bonusGX ? cadet.c4.bonusGX : '',
          cadet.c4 && cadet.c4.attend ? cadet.c4.attend : '',
          cadet.c4 && cadet.c4.penalty ? cadet.c4.penalty : '',
          cadet.c4 && cadet.c4.penaltyNotes ? cadet.c4.penaltyNotes : '',
          cadet.c4 && cadet.c4.total ? cadet.c4.total : '',
          // C5 data
          cadet.c5 && cadet.c5.recruit ? cadet.c5.recruit : '',
          cadet.c5 && cadet.c5.sales ? cadet.c5.sales : '',
          cadet.c5 && cadet.c5.book ? cadet.c5.book : '',
          cadet.c5 && cadet.c5.bonusPromo ? cadet.c5.bonusPromo : '',
          cadet.c5 && cadet.c5.bonusBBC ? cadet.c5.bonusBBC : '',
          cadet.c5 && cadet.c5.bonusEvents ? cadet.c5.bonusEvents : '',
          cadet.c5 && cadet.c5.bonusBF ? cadet.c5.bonusBF : '',
          cadet.c5 && cadet.c5.bonusGX ? cadet.c5.bonusGX : '',
          cadet.c5 && cadet.c5.attend ? cadet.c5.attend : '',
          cadet.c5 && cadet.c5.penalty ? cadet.c5.penalty : '',
          cadet.c5 && cadet.c5.penaltyNotes ? cadet.c5.penaltyNotes : '',
          cadet.c5 && cadet.c5.total ? cadet.c5.total : '',
          cadet.total
        ];
        worksheet.addRow(row);
      });

      // Auto-fit columns
      worksheet.columns.forEach((column: any) => {
        column.width = 15;
      });

      // Generate file and trigger download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'c3-scoreboard-after-callout.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // console.log(`Exported ${cadets.length} cadets to c3-scoreboard-after-callout.xlsx`);
    } catch (error) {
      console.error('Error exporting Class 3 Excel file:', error);
      throw new Error('Failed to export Class 3 Excel file');
    }
  }
}
